import { useEffect, useMemo, useState } from "react";
import { Box, Button, Grid, IconButton, Tooltip, Typography } from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PersonAddAlt1OutlinedIcon from "@mui/icons-material/PersonAddAlt1Outlined";
import { Link } from "react-router-dom";
import { api } from "../services/api";

const TYPES = ["str", "bool", "int", "float"];
const AI_MODEL_OPTIONS = ["openai", "gemini"];
const OPENAI_MODEL_OPTIONS = ["gpt-4.1-mini", "gpt-4.1", "gpt-5.4", "gpt-5.6-terra"];
const GEMINI_MODEL_OPTIONS = ["gemini-2.5-flash"];
const GPT5_REASONING_OPTIONS = ["low", "medium"];
const STT_ENGINE_OPTIONS = ["deepgram", "whisper", "webspeech"];
const TTS_ENGINE_OPTIONS = ["google", "native", "elevenlabs"];
const BOOLEAN_OPTIONS = ["true", "false"];
const USER_OVERRIDE_KEYS = [
  "ai_conversation_provider",
  "ai_evaluation_provider",
  "ai_evaluation_model",
  "ai_evaluation_reasoning_effort",
  "ai_conversation_analyzer_model",
  "ai_conversation_responder_model",
  "skip_mic_test",
  "stt_engine",
  "tts_engine",
];

const DEFAULT_NEW_APP_SETTING = {
  key: "",
  value: "",
  type: "str",
  frontend: false,
};

const DEFAULT_EDIT_APP_SETTING = {
  id: null,
  key: "",
  value: "",
  type: "str",
  frontend: false,
};

const DEFAULT_NEW_USER_SETTING = {
  user_id: "",
  key: "",
  value: "",
  type: "str",
};

const DEFAULT_EDIT_USER_SETTING = {
  id: null,
  user_id: "",
  user_full_name: "",
  user_email: "",
  key: "",
  value: "",
  type: "str",
};

const DEFAULT_DELETE_TARGET = {
  id: null,
  kind: "",
  label: "",
  userEmail: "",
};

const inputStyle = {
  padding: "6px 10px",
  borderRadius: 4,
  border: "1px solid #888",
  backgroundColor: "#222",
  color: "#fff",
};

const selectStyle = {
  padding: "6px 10px",
  borderRadius: 4,
  backgroundColor: "#222",
  color: "#fff",
  border: "1px solid #666",
};

const headerStyle = {
  padding: 8,
  backgroundColor: "#34163A",
  textAlign: "center",
  cursor: "pointer",
};

const normalizeSettingValue = (value) => (value ?? "").toString().trim().toLowerCase();
const GPT5_MODELS = ["gpt-5.4", "gpt-5.6-terra"];

const AdminSettings = () => {
  const [appSettings, setAppSettings] = useState([]);
  const [appSearchQuery, setAppSearchQuery] = useState("");
  const [appPage, setAppPage] = useState(1);
  const [appTotal, setAppTotal] = useState(0);
  const [appSortBy, setAppSortBy] = useState("key");
  const [appSortDir, setAppSortDir] = useState("asc");
  const [showAppForm, setShowAppForm] = useState(false);
  const [showAppEditForm, setShowAppEditForm] = useState(false);
  const [newAppSetting, setNewAppSetting] = useState(DEFAULT_NEW_APP_SETTING);
  const [editAppSetting, setEditAppSetting] = useState(DEFAULT_EDIT_APP_SETTING);

  const [userSettings, setUserSettings] = useState([]);
  const [userSearchQuery, setUserSearchQuery] = useState("");
  const [userPage, setUserPage] = useState(1);
  const [userTotal, setUserTotal] = useState(0);
  const [showUserForm, setShowUserForm] = useState(false);
  const [showUserEditForm, setShowUserEditForm] = useState(false);
  const [newUserSetting, setNewUserSetting] = useState(DEFAULT_NEW_USER_SETTING);
  const [editUserSetting, setEditUserSetting] = useState(DEFAULT_EDIT_USER_SETTING);
  const [deleteTarget, setDeleteTarget] = useState(DEFAULT_DELETE_TARGET);

  const [allUsers, setAllUsers] = useState([]);
  const [saving, setSaving] = useState(false);
  const limit = 20;

  const selectedUser = useMemo(
    () => allUsers.find((user) => user.id.toString() === newUserSetting.user_id?.toString()),
    [allUsers, newUserSetting.user_id]
  );

  const selectedEditUser = useMemo(
    () => allUsers.find((user) => user.id.toString() === editUserSetting.user_id?.toString()),
    [allUsers, editUserSetting.user_id]
  );

  const fetchAppSettings = async (pageToLoad = 1, query = appSearchQuery, sortBy = appSortBy, sortDir = appSortDir) => {
    const params = new URLSearchParams({
      page: pageToLoad,
      limit,
      sort_by: sortBy,
      sort_dir: sortDir,
      ...(query && { q: query }),
    }).toString();

    const res = await api.get(`/api/admin/app-settings/list?${params}`);
    setAppSettings(res.data.settings || []);
    setAppPage(res.data.page || 1);
    setAppTotal(res.data.total || 0);
  };

  const fetchUserSettings = async (pageToLoad = 1, query = userSearchQuery) => {
    const params = new URLSearchParams({
      page: pageToLoad,
      limit,
      ...(query && { q: query }),
    }).toString();

    const res = await api.get(`/api/admin/user-settings/list?${params}`);
    setUserSettings(res.data.settings || []);
    setUserPage(res.data.page || 1);
    setUserTotal(res.data.total || 0);
  };

  useEffect(() => {
    fetchAppSettings(1, "", "key", "asc");
    fetchUserSettings(1, "");

    const fetchUsers = async () => {
      const usersRes = await api.get("/api/admin/users/all");
      setAllUsers(usersRes.data || []);
    };

    fetchUsers();
  }, []);

  const appTotalPages = Math.max(1, Math.ceil(appTotal / limit));
  const userTotalPages = Math.max(1, Math.ceil(userTotal / limit));

  const getAppSettingValue = (key, excludedId = null) => {
    const match = appSettings.find((setting) => setting.key === key && setting.id !== excludedId);
    return match?.value ?? "";
  };

  const getUserSettingValue = (userId, key, excludedId = null) => {
    const match = userSettings.find(
      (setting) =>
        setting.user_id?.toString() === userId?.toString() &&
        setting.key === key &&
        setting.id !== excludedId
    );
    return match?.value ?? "";
  };

  const getProviderForSetting = ({ providerKey, providerSettingId, source, userId }) => {
    if (source === "user") {
      const userProvider = normalizeSettingValue(getUserSettingValue(userId, providerKey, providerSettingId));
      if (userProvider) {
        return userProvider;
      }
    }

    return normalizeSettingValue(getAppSettingValue(providerKey)) || "gemini";
  };

  const getEvaluationModelForSetting = ({ source, userId, settingId }) => {
    if (source === "user") {
      const userModel = (getUserSettingValue(userId, "ai_evaluation_model", settingId) || "").trim();
      if (userModel) {
        return userModel;
      }
    }

    return (getAppSettingValue("ai_evaluation_model") || "").trim();
  };

  const getSettingOptions = ({ key, value, source, userId, settingId }) => {
    if (key === "ai_conversation_provider") {
      return AI_MODEL_OPTIONS;
    }

    if (key === "ai_evaluation_provider") {
      return AI_MODEL_OPTIONS;
    }

    if (key === "ai_evaluation_model") {
      const provider = getProviderForSetting({
        providerKey: "ai_evaluation_provider",
        providerSettingId: settingId,
        source,
        userId,
      });
      return provider === "openai" ? OPENAI_MODEL_OPTIONS : GEMINI_MODEL_OPTIONS;
    }

    if (key === "ai_evaluation_reasoning_effort") {
      const modelName = getEvaluationModelForSetting({ source, userId, settingId });
      if ((modelName || "").startsWith("gpt-5")) {
        return GPT5_REASONING_OPTIONS;
      }
      return [""];
    }

    if (key === "ai_conversation_analyzer_model" || key === "ai_conversation_responder_model") {
      const provider = getProviderForSetting({
        providerKey: "ai_conversation_provider",
        providerSettingId: settingId,
        source,
        userId,
      });
      return provider === "openai" ? OPENAI_MODEL_OPTIONS : GEMINI_MODEL_OPTIONS;
    }

    if (key === "stt_engine") {
      return STT_ENGINE_OPTIONS;
    }

    if (key === "tts_engine") {
      return TTS_ENGINE_OPTIONS;
    }

    if (key === "skip_mic_test" || key === "stt_convert_to_linear16") {
      return BOOLEAN_OPTIONS;
    }

    return null;
  };

  const canCreateUserOverride = (key) => USER_OVERRIDE_KEYS.includes(key);

  const getEvaluationConsistencyWarning = ({ source, userId, nextKey, nextValue, settingId = null }) => {
    const getValue = (key) => {
      if (key === nextKey) {
        return nextValue;
      }

      if (source === "user") {
        const userValue = getUserSettingValue(userId, key, settingId);
        if (userValue !== "") {
          return userValue;
        }
      }

      return getAppSettingValue(key);
    };

    const provider = normalizeSettingValue(getValue("ai_evaluation_provider")) || "gemini";
    const model = (getValue("ai_evaluation_model") || "").trim();
    const effort = normalizeSettingValue(getValue("ai_evaluation_reasoning_effort"));

    if (provider === "gemini") {
      if (model && model !== "gemini-2.5-flash") {
        return "Saved. Review ai_evaluation_model: Gemini should use gemini-2.5-flash.";
      }
      if (effort) {
        return "Saved. Review ai_evaluation_reasoning_effort: it should be empty when evaluation provider/model do not use GPT-5 reasoning.";
      }
      return null;
    }

    if (provider === "openai") {
      const validOpenAIModels = OPENAI_MODEL_OPTIONS;
      if (model && !validOpenAIModels.includes(model)) {
        return "Saved. Review ai_evaluation_model: current value does not match the selected OpenAI provider.";
      }

      const usesReasoning = GPT5_MODELS.includes(model);
      if (usesReasoning && !GPT5_REASONING_OPTIONS.includes(effort)) {
        return "Saved. Review ai_evaluation_reasoning_effort: GPT-5 evaluation models should use low or medium.";
      }

      if (!usesReasoning && effort) {
        return "Saved. Review ai_evaluation_reasoning_effort: it should be empty for gpt-4.1 / gpt-4.1-mini.";
      }
    }

    return null;
  };

  const normalizeFormValueForKey = (formState, source, nextKey = formState.key) => {
    const options = getSettingOptions({
      key: nextKey,
      value: formState.value,
      source,
      userId: formState.user_id,
      settingId: formState.id,
    });

    if (!options?.length) {
      return {
        ...formState,
        key: nextKey,
      };
    }

    return {
      ...formState,
      key: nextKey,
      value: options.includes(formState.value) ? formState.value : options[0],
    };
  };

  const openUserOverrideForm = (setting) => {
    setNewUserSetting(normalizeFormValueForKey({
      user_id: "",
      key: setting.key,
      value: setting.value ?? "",
      type: setting.type,
    }, "user"));
    setShowUserForm(true);
  };

  const renderValueInput = ({ formState, setFormState, source }) => {
    const options = getSettingOptions({
      key: formState.key,
      value: formState.value,
      source,
      userId: formState.user_id,
      settingId: formState.id,
    });

    if (!options) {
      return (
        <input
          type="text"
          placeholder="Value"
          value={formState.value}
          onChange={(e) => setFormState({ ...formState, value: e.target.value })}
          style={inputStyle}
        />
      );
    }

    const currentValue = options.includes(formState.value) ? formState.value : options[0];

    return (
      <select
        value={currentValue}
        onChange={(e) => setFormState({ ...formState, value: e.target.value })}
        style={selectStyle}
      >
        {options.map((option) => (
          <option key={option} value={option}>
            {option || "Not used"}
          </option>
        ))}
      </select>
    );
  };

  const renderModal = (title, content) => (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        backgroundColor: "rgba(0,0,0,0.6)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
      }}
    >
      <Box
        sx={{
          backgroundColor: "#452654",
          borderRadius: 2,
          p: 4,
          minWidth: 420,
          maxWidth: "92vw",
          boxShadow: 6,
        }}
      >
        <Typography variant="body2" sx={{ mb: 2, fontWeight: "bold", color: "#fff" }}>
          {title}
        </Typography>
        {content}
      </Box>
    </Box>
  );

  const handleAppSort = (field) => {
    const nextDir = appSortBy === field && appSortDir === "asc" ? "desc" : "asc";
    setAppSortBy(field);
    setAppSortDir(nextDir);
    fetchAppSettings(1, appSearchQuery, field, nextDir);
  };

  const handleCreateAppSetting = async () => {
    if (!newAppSetting.key || !newAppSetting.type) {
      alert("Pitchest Admin\n\nKey and type are required.");
      return;
    }

    try {
      setSaving(true);
      await api.post("/api/admin/app-settings", newAppSetting);
      setNewAppSetting(DEFAULT_NEW_APP_SETTING);
      setShowAppForm(false);
      fetchAppSettings(1, appSearchQuery, appSortBy, appSortDir);
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.error || "Error saving app setting");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateAppSetting = async () => {
    if (!editAppSetting.id || !editAppSetting.type) {
      alert("Pitchest Admin\n\nMissing app setting data.");
      return;
    }

    try {
      setSaving(true);
      await api.patch(`/api/admin/app-settings/${editAppSetting.id}`, {
        value: editAppSetting.value,
        type: editAppSetting.type,
        frontend: editAppSetting.frontend,
      });
      const warning = getEvaluationConsistencyWarning({
        source: "app",
        nextKey: editAppSetting.key,
        nextValue: editAppSetting.value,
        settingId: editAppSetting.id,
      });
      setEditAppSetting(DEFAULT_EDIT_APP_SETTING);
      setShowAppEditForm(false);
      fetchAppSettings(appPage, appSearchQuery, appSortBy, appSortDir);
      if (warning) {
        alert(`Pitchest Admin\n\n${warning}`);
      }
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.error || "Error updating app setting");
    } finally {
      setSaving(false);
    }
  };

  const handleCreateUserSetting = async () => {
    if (!newUserSetting.user_id || !newUserSetting.key || !newUserSetting.type) {
      alert("Pitchest Admin\n\nUser, key and type are required.");
      return;
    }

    try {
      setSaving(true);
      await api.post("/api/admin/user-settings", {
        ...newUserSetting,
        user_id: Number(newUserSetting.user_id),
      });
      const warning = getEvaluationConsistencyWarning({
        source: "user",
        userId: newUserSetting.user_id,
        nextKey: newUserSetting.key,
        nextValue: newUserSetting.value,
      });
      setNewUserSetting(DEFAULT_NEW_USER_SETTING);
      setShowUserForm(false);
      fetchUserSettings(1, userSearchQuery);
      if (warning) {
        alert(`Pitchest Admin\n\n${warning}`);
      }
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.error || "Error saving user setting");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateUserSetting = async () => {
    if (!editUserSetting.id || !editUserSetting.key || !editUserSetting.type) {
      alert("Pitchest Admin\n\nMissing user setting data.");
      return;
    }

    try {
      setSaving(true);
      await api.patch(`/api/admin/user-settings/${editUserSetting.id}`, {
        key: editUserSetting.key,
        value: editUserSetting.value,
        type: editUserSetting.type,
      });
      const warning = getEvaluationConsistencyWarning({
        source: "user",
        userId: editUserSetting.user_id,
        nextKey: editUserSetting.key,
        nextValue: editUserSetting.value,
        settingId: editUserSetting.id,
      });
      setEditUserSetting(DEFAULT_EDIT_USER_SETTING);
      setShowUserEditForm(false);
      fetchUserSettings(userPage, userSearchQuery);
      if (warning) {
        alert(`Pitchest Admin\n\n${warning}`);
      }
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.error || "Error updating user setting");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box
      sx={{
        backgroundImage: 'url("/assets/bg-pitchest.jpg")',
        backgroundSize: "cover",
        minHeight: "100vh",
        px: 2,
        py: 6,
      }}
    >
      <Box sx={{ backgroundColor: "rgba(0,0,0,0.6)", borderRadius: 4, p: { xs: 2, md: 4 }, color: "white" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
          <Typography variant="h5">Settings Admin</Typography>
          <Link to="/admin" style={{ color: "#90caf9", fontWeight: "bold" }}>Back</Link>
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} lg={6}>
            <Box sx={{ backgroundColor: "rgba(38, 18, 46, 0.92)", borderRadius: 3, p: 2.5, height: "100%" }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="h6">App Settings</Typography>
                <Button variant="outlined" onClick={() => setShowAppForm(true)}>New App Setting</Button>
              </Box>

              <Box sx={{ display: "flex", mb: 2, gap: 1, flexWrap: "wrap" }}>
                <input
                  type="text"
                  placeholder="Search by key, value or type"
                  value={appSearchQuery}
                  onChange={(e) => setAppSearchQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      e.stopPropagation();
                      fetchAppSettings(1, e.target.value, appSortBy, appSortDir);
                    }
                  }}
                  style={{ ...inputStyle, width: 260 }}
                />
                <Button variant="outlined" onClick={() => fetchAppSettings(1, appSearchQuery, appSortBy, appSortDir)} size="small">
                  🔍 Search
                </Button>
                <Typography variant="body2" sx={{ color: "#ddd", display: "flex", alignItems: "center", px: 1 }}>
                  Total: {appTotal}
                </Typography>
              </Box>

              <Box sx={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", color: "white" }}>
                  <thead>
                    <tr>
                      {[
                        { label: "Key", field: "key" },
                        { label: "Value", field: "value" },
                        { label: "Type", field: "type" },
                        { label: "Frontend", field: "frontend" },
                      ].map(({ label, field }) => (
                        <th key={field} onClick={() => handleAppSort(field)} style={headerStyle}>
                          {label} {appSortBy === field && (appSortDir === "asc" ? "▲" : "▼")}
                        </th>
                      ))}
                      <th style={{ ...headerStyle, cursor: "default" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {appSettings.map((setting, idx) => (
                      <tr key={setting.id} style={{ backgroundColor: idx % 2 === 0 ? "#5E2F6E" : "transparent" }}>
                        <td style={{ padding: 8, textAlign: "center" }}>{setting.key}</td>
                        <td style={{ padding: 8, textAlign: "center", wordBreak: "break-word" }}>{setting.value}</td>
                        <td style={{ padding: 8, textAlign: "center" }}>{setting.type}</td>
                        <td style={{ padding: 8, textAlign: "center" }}>{setting.frontend ? "Yes" : "No"}</td>
                        <td style={{ padding: 8, textAlign: "center" }}>
                          <Tooltip title="Edit app setting">
                            <IconButton
                              size="small"
                              sx={{ color: "#90caf9" }}
                              onClick={() => {
                                setEditAppSetting(normalizeFormValueForKey({
                                  id: setting.id,
                                  key: setting.key,
                                  value: setting.value ?? "",
                                  type: setting.type,
                                  frontend: setting.frontend,
                                }, "app"));
                                setShowAppEditForm(true);
                              }}
                            >
                              <EditOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {canCreateUserOverride(setting.key) && (
                            <Tooltip title="Create user override">
                              <IconButton
                                size="small"
                                sx={{ color: "#A5D6A7" }}
                                onClick={() => openUserOverrideForm(setting)}
                              >
                                <PersonAddAlt1OutlinedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </td>
                      </tr>
                    ))}
                    {appSettings.length === 0 && (
                      <tr>
                        <td colSpan={5} style={{ textAlign: "center", padding: 16, backgroundColor: "#452654" }}>
                          No app settings found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </Box>

              <Box sx={{ mt: 3, display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
                <Button variant="outlined" disabled={appPage === 1} onClick={() => fetchAppSettings(appPage - 1, appSearchQuery, appSortBy, appSortDir)}>Prev</Button>
                <Button variant="outlined" disabled={appPage >= appTotalPages} onClick={() => fetchAppSettings(appPage + 1, appSearchQuery, appSortBy, appSortDir)}>Next</Button>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} lg={6}>
            <Box sx={{ backgroundColor: "rgba(38, 18, 46, 0.92)", borderRadius: 3, p: 2.5, height: "100%" }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="h6">User Settings</Typography>
              </Box>

              <Box sx={{ display: "flex", mb: 2, gap: 1, flexWrap: "wrap" }}>
                <input
                  type="text"
                  placeholder="Search by user, email, key or value"
                  value={userSearchQuery}
                  onChange={(e) => setUserSearchQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      e.stopPropagation();
                      fetchUserSettings(1, e.target.value);
                    }
                  }}
                  style={{ ...inputStyle, width: 280 }}
                />
                <Button variant="outlined" onClick={() => fetchUserSettings(1, userSearchQuery)} size="small">
                  🔍 Search
                </Button>
                <Typography variant="body2" sx={{ color: "#ddd", display: "flex", alignItems: "center", px: 1 }}>
                  Total: {userTotal}
                </Typography>
              </Box>

              <Box sx={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", color: "white" }}>
                  <thead>
                    <tr>
                      {["ID", "User", "Email", "Key", "Value", "Type", "Actions"].map((label) => (
                        <th key={label} style={{ padding: 8, backgroundColor: "#34163A", textAlign: "center" }}>
                          {label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {userSettings.map((setting, idx) => (
                      <tr key={setting.id} style={{ backgroundColor: idx % 2 === 0 ? "#5E2F6E" : "transparent" }}>
                        <td style={{ padding: 8, textAlign: "center" }}>{setting.user_id}</td>
                        <td style={{ padding: 8, textAlign: "center" }}>{setting.user_full_name || "-"}</td>
                        <td style={{ padding: 8, textAlign: "center" }}>{setting.user_email || "-"}</td>
                        <td style={{ padding: 8, textAlign: "center" }}>{setting.key}</td>
                        <td style={{ padding: 8, textAlign: "center", wordBreak: "break-word" }}>{setting.value ?? "-"}</td>
                        <td style={{ padding: 8, textAlign: "center" }}>{setting.type}</td>
                        <td style={{ padding: 8, textAlign: "center" }}>
                          <Tooltip title="Edit user setting">
                            <IconButton
                              size="small"
                              sx={{ color: "#90caf9" }}
                              onClick={() => {
                                setEditUserSetting(normalizeFormValueForKey({
                                  id: setting.id,
                                  user_id: setting.user_id?.toString() || "",
                                  user_full_name: setting.user_full_name || "",
                                  user_email: setting.user_email || "",
                                  key: setting.key,
                                  value: setting.value ?? "",
                                  type: setting.type,
                                }, "user"));
                                setShowUserEditForm(true);
                              }}
                            >
                              <EditOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete user setting">
                            <IconButton
                              size="small"
                              sx={{ color: "#ff8a80" }}
                              onClick={() => {
                                setDeleteTarget({
                                  id: setting.id,
                                  kind: "user",
                                  label: setting.key,
                                  userEmail: setting.user_email || "",
                                });
                              }}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </td>
                      </tr>
                    ))}
                    {userSettings.length === 0 && (
                      <tr>
                        <td colSpan={7} style={{ textAlign: "center", padding: 16, backgroundColor: "#452654" }}>
                          No user settings found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </Box>

              <Box sx={{ mt: 3, display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
                <Button variant="outlined" disabled={userPage === 1} onClick={() => fetchUserSettings(userPage - 1, userSearchQuery)}>Prev</Button>
                <Button variant="outlined" disabled={userPage >= userTotalPages} onClick={() => fetchUserSettings(userPage + 1, userSearchQuery)}>Next</Button>
              </Box>
            </Box>
          </Grid>
        </Grid>

        {showAppForm && renderModal(
          "Create New App Setting",
          <>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 3 }}>
              <input
                type="text"
                placeholder="Key"
                value={newAppSetting.key}
                onChange={(e) => setNewAppSetting(normalizeFormValueForKey(newAppSetting, "app", e.target.value))}
                style={inputStyle}
              />
              {renderValueInput({
                formState: newAppSetting,
                setFormState: setNewAppSetting,
                source: "app",
              })}
              <select
                value={newAppSetting.type}
                onChange={(e) => setNewAppSetting({ ...newAppSetting, type: e.target.value })}
                style={selectStyle}
              >
                {TYPES.map((type) => <option key={type} value={type}>{type}</option>)}
              </select>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <label style={{ color: "#fff", fontSize: 14 }}>Frontend visible:</label>
                <input
                  type="checkbox"
                  checked={newAppSetting.frontend}
                  onChange={(e) => setNewAppSetting({ ...newAppSetting, frontend: e.target.checked })}
                />
              </Box>
            </Box>
            <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
              <Button variant="contained" disabled={saving} onClick={handleCreateAppSetting}>
                {saving ? "Saving..." : "Save"}
              </Button>
              <Button
                variant="outlined"
                color="error"
                disabled={saving}
                onClick={() => {
                  setNewAppSetting(DEFAULT_NEW_APP_SETTING);
                  setShowAppForm(false);
                }}
              >
                Cancel
              </Button>
            </Box>
          </>
        )}

        {showAppEditForm && renderModal(
          "Edit App Setting",
          <>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 3 }}>
              <input
                type="text"
                value={editAppSetting.key}
                disabled
                style={{ ...inputStyle, opacity: 0.7, cursor: "not-allowed" }}
              />
              {renderValueInput({
                formState: editAppSetting,
                setFormState: setEditAppSetting,
                source: "app",
              })}
              <select
                value={editAppSetting.type}
                onChange={(e) => setEditAppSetting({ ...editAppSetting, type: e.target.value })}
                style={selectStyle}
              >
                {TYPES.map((type) => <option key={type} value={type}>{type}</option>)}
              </select>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <label style={{ color: "#fff", fontSize: 14 }}>Frontend visible:</label>
                <input
                  type="checkbox"
                  checked={editAppSetting.frontend}
                  disabled
                  style={{ cursor: "not-allowed" }}
                />
              </Box>
            </Box>
            <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
              <Button variant="contained" disabled={saving} onClick={handleUpdateAppSetting}>
                {saving ? "Saving..." : "Save"}
              </Button>
              <Button
                variant="outlined"
                color="error"
                disabled={saving}
                onClick={() => {
                  setEditAppSetting(DEFAULT_EDIT_APP_SETTING);
                  setShowAppEditForm(false);
                }}
              >
                Cancel
              </Button>
            </Box>
          </>
        )}

        {showUserForm && renderModal(
          "Create User Setting Override",
          <>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 3 }}>
              <input
                list="user-setting-users"
                value={newUserSetting.user_id}
                onChange={(e) => setNewUserSetting({ ...newUserSetting, user_id: e.target.value })}
                placeholder="User ID"
                style={inputStyle}
              />
              <datalist id="user-setting-users">
                {allUsers.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name} ({user.email})
                  </option>
                ))}
              </datalist>
              {selectedUser && (
                <Typography variant="body2" sx={{ color: "#ccc" }}>
                  {selectedUser.name} - {selectedUser.email}
                </Typography>
              )}
              <input
                type="text"
                value={newUserSetting.key}
                disabled
                style={{ ...inputStyle, opacity: 0.7, cursor: "not-allowed" }}
              />
              {renderValueInput({
                formState: newUserSetting,
                setFormState: setNewUserSetting,
                source: "user",
              })}
              <select
                value={newUserSetting.type}
                disabled
                style={{ ...selectStyle, opacity: 0.7, cursor: "not-allowed" }}
              >
                {TYPES.map((type) => <option key={type} value={type}>{type}</option>)}
              </select>
            </Box>
            <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
              <Button variant="contained" disabled={saving} onClick={handleCreateUserSetting}>
                {saving ? "Saving..." : "Save"}
              </Button>
              <Button
                variant="outlined"
                color="error"
                disabled={saving}
                onClick={() => {
                  setNewUserSetting(DEFAULT_NEW_USER_SETTING);
                  setShowUserForm(false);
                }}
              >
                Cancel
              </Button>
            </Box>
          </>
        )}

        {showUserEditForm && renderModal(
          "Edit User Setting",
          <>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 3 }}>
              <input
                type="text"
                value={editUserSetting.user_id}
                disabled
                style={{ ...inputStyle, opacity: 0.7, cursor: "not-allowed" }}
              />
              {(editUserSetting.user_full_name || editUserSetting.user_email || selectedEditUser) && (
                <Typography variant="body2" sx={{ color: "#ccc" }}>
                  {selectedEditUser?.name || editUserSetting.user_full_name || "-"}
                  {" - "}
                  {selectedEditUser?.email || editUserSetting.user_email || "-"}
                </Typography>
              )}
              <input
                type="text"
                value={editUserSetting.key}
                disabled
                style={{ ...inputStyle, opacity: 0.7, cursor: "not-allowed" }}
              />
              {renderValueInput({
                formState: editUserSetting,
                setFormState: setEditUserSetting,
                source: "user",
              })}
              <select
                value={editUserSetting.type}
                onChange={(e) => setEditUserSetting({ ...editUserSetting, type: e.target.value })}
                style={selectStyle}
              >
                {TYPES.map((type) => <option key={type} value={type}>{type}</option>)}
              </select>
            </Box>
            <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
              <Button variant="contained" disabled={saving} onClick={handleUpdateUserSetting}>
                {saving ? "Saving..." : "Save"}
              </Button>
              <Button
                variant="outlined"
                color="error"
                disabled={saving}
                onClick={() => {
                  setEditUserSetting(DEFAULT_EDIT_USER_SETTING);
                  setShowUserEditForm(false);
                }}
              >
                Cancel
              </Button>
            </Box>
          </>
        )}

        {deleteTarget.id && renderModal(
          "Confirm Delete",
          <>
            <Box sx={{ mb: 3, color: "#fff" }}>
              <Typography variant="body2">
                {deleteTarget.kind === "app"
                  ? `Are you sure you want to delete the app setting "${deleteTarget.label}"?`
                  : `Are you sure you want to delete the user setting "${deleteTarget.label}" for ${deleteTarget.userEmail}?`}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                color="error"
                disabled={saving}
                onClick={async () => {
                  try {
                    setSaving(true);
                    if (deleteTarget.kind === "app") {
                      await api.delete(`/api/admin/app-settings/${deleteTarget.id}`);
                      fetchAppSettings(appPage, appSearchQuery, appSortBy, appSortDir);
                    } else {
                      await api.delete(`/api/admin/user-settings/${deleteTarget.id}`);
                      fetchUserSettings(userPage, userSearchQuery);
                    }
                    setDeleteTarget(DEFAULT_DELETE_TARGET);
                  } catch (err) {
                    console.error(err);
                    alert("Error deleting setting");
                  } finally {
                    setSaving(false);
                  }
                }}
              >
                {saving ? "Deleting..." : "Delete"}
              </Button>
              <Button
                variant="outlined"
                disabled={saving}
                onClick={() => setDeleteTarget(DEFAULT_DELETE_TARGET)}
              >
                Cancel
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default AdminSettings;
